import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import "./SelectVoiceSection.css";
import { resetAudioElement } from '../../utils/audio';

function SelectVoiceSection({
  disabled,
  greeting,
  defaultLanguage = 'en',
  onDraftChange,
  onVoiceOptionsLoaded,
  voiceId,
}) {
  const [voiceOptions, setVoiceOptions] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const audioRef = useRef(null);
  const previewRequest = useRef(0);
  const [previewError, setPreviewError] = useState('');

  useEffect(() => {
    previewRequest.current += 1;
    resetAudioElement(audioRef.current);
    setIsPlaying(false);
    setIsLoading(false);
    setPreviewError('');
    return () => { previewRequest.current += 1; };
  }, [greeting, voiceId, defaultLanguage]);
  const selectedVoice = voiceId || '';

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_BACKEND_HOST}/voice-list`)
      .then(response => {
        setVoiceOptions(response.data);
        onVoiceOptionsLoaded(response.data);
      })
      .catch(error => {
        console.error('Error fetching voice list:', error);
      });
  }, [onVoiceOptionsLoaded]);

  useEffect(() => {
    const audio = audioRef.current;
    const handleAudioEnd = () => {
      setIsPlaying(false);
    };
    const handleCanPlayThrough = () => {
      audio.play();
      setIsPlaying(true);
      setIsLoading(false);
    };

    audio.addEventListener("ended", handleAudioEnd);
    audio.addEventListener("canplaythrough", handleCanPlayThrough);

    return () => {
      audio.removeEventListener("ended", handleAudioEnd);
      audio.removeEventListener("canplaythrough", handleCanPlayThrough);
    };
  }, []);

  const handleVoiceChange = (e) => {
    const nextVoice = e.target.value;
    onDraftChange({ voiceId: nextVoice });
  };

  const handlePlayClicked = () => {
    setIsLoading(true);
    handlePreviewClicked();
  };

  const handleStopClicked = () => {
    previewRequest.current += 1;
    resetAudioElement(audioRef.current);
    setIsPlaying(false);
    setIsLoading(false);
  };

  const handlePreviewClicked = async () => {
    const requestId = ++previewRequest.current;
    setPreviewError('');
    const jwt_token = localStorage.getItem('jwt_authorization');
    resetAudioElement(audioRef.current);

    axios.post(`${process.env.REACT_APP_BACKEND_HOST}/preview-voice`, { 
      voice_id: selectedVoice,
      text: greeting,
      default_language: defaultLanguage,
      jwt_token 
    })
      .then(response => {
        if (requestId !== previewRequest.current) return;

        if (process.env.REACT_APP_ENV !== 'dev') {
           audioRef.current.src = response.data.preview_url;
        } else {
          // replace https:// with http:// for dev mode
          audioRef.current.src = response.data.preview_url.replace('https://', 'http://');
        }

      })
      .catch(error => {
        if (requestId !== previewRequest.current) return;
        setPreviewError('Could not prepare the greeting in the selected language. Please try again.');
        console.error('Error fetching voice preview:', error);
        setIsLoading(false);
      });
  };

  return (
    <section className="receptionist-voice-card">
      <div className="receptionist-setting-heading">
        <span className="receptionist-setting-icon voice-icon" aria-hidden="true">◉</span>
        <div><h2>Receptionist voice</h2><p>Choose the voice callers will speak with.</p></div>
      </div>
      {previewError ? <p role="alert">{previewError}</p> : null}
      <div className="receptionist-voice-controls">
        <label className="receptionist-voice-field">
          <span>Voice</span>
          <select data-say-id="ai-voice-select" className="receptionist-voice-select" value={selectedVoice} onChange={handleVoiceChange} disabled={disabled}>
            {!selectedVoice ? <option value="" disabled>Choose a voice</option> : null}
            {voiceOptions.map((voice, index) => (
              <option data-say-id="ai-voice-option" key={index} value={voice.id}>{voice.name} | {voice.style}</option>
            ))}
          </select>
        </label>
        <div className="receptionist-preview-control">
          <audio ref={audioRef}></audio>
          {isPlaying ? (
            <button type="button" className="receptionist-preview-button" onClick={handleStopClicked}>Stop</button>
          ) : (
            <button type="button" className="receptionist-preview-button" onClick={handlePlayClicked} disabled={isLoading || !selectedVoice || !greeting?.trim()}>
              {isLoading ? <span className="receptionist-voice-spinner" aria-label="Loading preview" /> : 'Play greeting'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export default SelectVoiceSection;
